import { useCallback, useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { CheckCircle2, XCircle, ScanLine, Loader2, Camera, CameraOff, MapPin } from "lucide-react";
import { endpoints } from "@/lib/api";
import { PageHeader, Card, Btn } from "./AdminUI";

const RESULT_MS = 5000; // show the result screen for exactly 5 seconds, then resume scanning

// Short beep via Web Audio — success (high, pleasant) vs error (low, buzzer).
function beep(ok) {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ac = new Ctx();
    const o = ac.createOscillator();
    const g = ac.createGain();
    o.connect(g); g.connect(ac.destination);
    o.type = ok ? "sine" : "square";
    o.frequency.value = ok ? 880 : 220;
    g.gain.setValueAtTime(0.001, ac.currentTime);
    g.gain.exponentialRampToValueAtTime(0.25, ac.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + (ok ? 0.18 : 0.4));
    o.start();
    o.stop(ac.currentTime + (ok ? 0.2 : 0.45));
    if (!ok) {
      const o2 = ac.createOscillator();
      o2.connect(g); o2.type = "square"; o2.frequency.value = 180;
      o2.start(ac.currentTime + 0.2); o2.stop(ac.currentTime + 0.45);
    }
    setTimeout(() => { try { ac.close(); } catch { /* noop */ } }, 800);
  } catch { /* noop */ }
}

export default function Scanner() {
  const [code, setCode] = useState("");
  const [result, setResult] = useState(null);   // last scan result -> shows full-screen overlay
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(true); // camera starts automatically
  const [secureError, setSecureError] = useState("");
  const [countdown, setCountdown] = useState(0);

  const qrRef = useRef(null);
  const lockRef = useRef(false);   // true while a result overlay is showing (pause decoding)
  const timerRef = useRef(null);

  const startCamera = useCallback(async () => {
    if (!window.isSecureContext || !navigator.mediaDevices) {
      setSecureError("Camera needs a secure (HTTPS) page and camera access. Open the site directly on your phone over HTTPS and allow the camera, or type the ticket number below.");
      setScanning(false);
      return;
    }
    if (qrRef.current) return;
    const el = document.getElementById("qr-reader");
    if (!el) return;
    const qr = new Html5Qrcode("qr-reader", { verbose: false });
    qrRef.current = qr;
    const config = { fps: 10, qrbox: { width: 260, height: 260 } };
    const onDecoded = (decoded) => {
      if (lockRef.current) return;      // ignore scans while a result is being shown
      lockRef.current = true;
      check(decoded);
    };
    try {
      await qr.start({ facingMode: "environment" }, config, onDecoded, () => {});
    } catch (e) {
      setSecureError("Could not open the camera. Allow camera access in your browser, or type the ticket number below.");
      qrRef.current = null;
      setScanning(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopCamera = useCallback(async () => {
    const qr = qrRef.current;
    qrRef.current = null;
    if (qr) { try { await qr.stop(); } catch { /* noop */ } try { await qr.clear(); } catch { /* noop */ } }
  }, []);

  // Mount / unmount camera when `scanning` toggles.
  useEffect(() => {
    if (scanning) { startCamera(); }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [scanning, startCamera]);

  useEffect(() => () => { stopCamera(); }, [stopCamera]);

  const resumeScanning = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setResult(null);
    setCountdown(0);
    lockRef.current = false;
  }, []);

  const showResult = useCallback((data) => {
    beep(!!data?.valid);
    setResult(data);
    let left = RESULT_MS;
    setCountdown(Math.ceil(left / 1000));
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      left -= 250;
      setCountdown(Math.max(0, Math.ceil(left / 1000)));
      if (left <= 0) { resumeScanning(); }
    }, 250);
  }, [resumeScanning]);

  const check = async (value) => {
    const c = (value || "").trim();
    if (!c) { lockRef.current = false; return; }
    setBusy(true);
    try {
      const { data } = await endpoints.adminScanTicket(c);
      showResult(data);
    } catch {
      showResult({ valid: false, reason: "Scan failed — try again" });
    } finally { setBusy(false); setCode(""); }
  };

  // Manual verify (typed) — pause decoding while its result shows too.
  const manualCheck = () => { if (!lockRef.current) { lockRef.current = true; check(code); } };

  const toggleCamera = async () => {
    if (scanning) { await stopCamera(); setScanning(false); }
    else { setSecureError(""); resumeScanning(); setScanning(true); }
  };

  const ok = result?.valid;
  const statusText = ok ? "VALID — ENTRY GRANTED"
    : result?.status === "used" ? "ALREADY USED"
    : result?.status === "cancelled" ? "CANCELLED" : "INVALID";

  return (
    <div>
      <PageHeader title="Entrance Scanner" desc="Point the camera at a ticket QR. Valid tickets are checked in automatically — the camera keeps scanning hands-free." />

      {/* Full-screen result overlay (5s) */}
      {result && (
        <div
          data-testid="scan-overlay"
          className={`fixed inset-0 z-[9999] flex flex-col items-center justify-center text-center px-6 ${ok ? "bg-emerald-600" : "bg-red-700"}`}
        >
          {ok
            ? <CheckCircle2 className="w-28 h-28 text-white mb-4" data-testid="scan-valid" />
            : <XCircle className="w-28 h-28 text-white mb-4" data-testid="scan-invalid" />}
          <div className="text-white text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid="scan-status">{statusText}</div>
          <div className="text-white/90 text-base mt-1">{result.reason}</div>

          {result.ticketNumber && (
            <div className="mt-6 space-y-1.5">
              <div className="text-white font-extrabold text-2xl" data-testid="scan-event">{result.eventTitle}</div>
              <div className="text-white/90 text-lg" data-testid="scan-holder">{result.holderName}</div>
              {result.seat && (
                <div className="inline-flex items-center gap-2 mt-1 px-4 py-1.5 rounded-full bg-white/20 text-white text-xl font-extrabold" data-testid="scan-seat">
                  <MapPin className="w-5 h-5" /> {result.seat}
                </div>
              )}
              <div className="text-white/80 text-sm">{result.categoryName}</div>
              <div className="text-[#FFE28A] font-extrabold text-xl tracking-wide" data-testid="scan-ticketnumber">{result.ticketNumber}</div>
              {result.isInvitation && <div className="text-white/90 text-xs font-bold">INVITATION</div>}
              {!ok && result.checkedInAt && <div className="text-white/80 text-xs">Scanned before: {new Date(result.checkedInAt).toLocaleString()}</div>}
            </div>
          )}

          <div className="mt-8 flex flex-col items-center gap-3">
            <div className="text-white/90 text-sm">Next scan in {countdown}s…</div>
            <button onClick={resumeScanning} data-testid="scan-next-btn"
              className="h-11 px-6 rounded-full bg-white text-slate-900 font-bold hover:bg-white/90 transition-colors">
              Scan next now
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-5">
          {!scanning ? (
            <div className="rounded-xl bg-black min-h-[240px] flex flex-col items-center justify-center text-slate-500 gap-3" data-testid="qr-idle">
              <ScanLine className="w-10 h-10" />
              <Btn onClick={toggleCamera} data-testid="cam-start"><Camera className="w-4 h-4" /> Start camera</Btn>
            </div>
          ) : (
            <>
              <div id="qr-reader" className="w-full rounded-xl overflow-hidden bg-black scanner-box" data-testid="qr-reader" />
              <div className="flex items-center gap-2 mt-3">
                <Btn variant="ghost" onClick={toggleCamera} data-testid="cam-stop"><CameraOff className="w-4 h-4" /> Stop camera</Btn>
                <span className="text-xs text-emerald-400 flex items-center gap-1.5" data-testid="cam-live">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> Ready — scanning
                </span>
                {busy && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
              </div>
            </>
          )}
          {secureError && <div className="mt-2 text-xs text-amber-400" data-testid="cam-error">{secureError}</div>}
          <div className="mt-4 flex gap-2">
            <input value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => e.key === "Enter" && manualCheck()}
              placeholder="Or type / paste ticket number or code" data-testid="scan-input"
              className="flex-1 min-w-0 h-11 rounded-xl bg-[#0A0C10] border border-[#273042] px-4 text-white outline-none focus:border-[#E11D2A]/60" />
            <Btn onClick={manualCheck} disabled={busy} data-testid="scan-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify"}</Btn>
          </div>
        </Card>

        <Card className="p-6 flex flex-col items-center justify-center text-center min-h-[300px]" data-testid="scan-instructions">
          <ScanLine className="w-12 h-12 mx-auto mb-3 text-slate-500" />
          <div className="text-white font-bold">Hands-free entrance mode</div>
          <p className="text-slate-400 text-sm mt-2 max-w-xs">
            Hold a ticket QR in front of the camera. A full-screen <span className="text-emerald-400 font-semibold">green</span> or
            <span className="text-red-400 font-semibold"> red</span> result shows for 5 seconds, then the camera automatically
            resumes for the next ticket — no tapping needed.
          </p>
        </Card>
      </div>
    </div>
  );
}
